import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../config/firebase.config';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [idToken, setIdToken] = useState(null);
    const [mustChangePassword, setMustChangePassword] = useState(false);

    useEffect(() => {
        // Listen to auth state changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    // Get ID token
                    const token = await firebaseUser.getIdToken();
                    setIdToken(token);

                    // Get user profile from Firestore
                    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setUser({
                            id: firebaseUser.uid,
                            email: firebaseUser.email,
                            fullName: userData.fullName,
                            role: userData.role,
                            department: userData.department,
                            phone: userData.phone,
                            isActive: userData.isActive,
                            mustChangePassword: userData.mustChangePassword || false
                        });
                        setMustChangePassword(userData.mustChangePassword || false);
                    } else {
                        console.error('User profile not found in Firestore');
                        setUser(null);
                    }
                } catch (error) {
                    console.error('Error loading user profile:', error);
                    setUser(null);
                }
            } else {
                setUser(null);
                setIdToken(null);
                setMustChangePassword(false);
            }
            setLoading(false);
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        try {
            setLoading(true);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            // Get ID token
            const token = await userCredential.user.getIdToken();
            setIdToken(token);

            // Get user profile from Firestore
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

            if (!userDoc.exists()) {
                throw new Error('User profile not found');
            }

            const userData = userDoc.data();

            if (!userData.isActive) {
                await signOut(auth);
                throw new Error('Account is disabled. Please contact administrator.');
            }

            const userProfile = {
                id: userCredential.user.uid,
                email: userCredential.user.email,
                fullName: userData.fullName,
                role: userData.role,
                department: userData.department,
                phone: userData.phone,
                isActive: userData.isActive,
                mustChangePassword: userData.mustChangePassword || false
            };

            setUser(userProfile);
            setLoading(false);

            return userProfile;
        } catch (error) {
            setLoading(false);
            console.error('Login error:', error);

            // Provide user-friendly error messages
            if (error.code === 'auth/user-not-found') {
                throw new Error('No account found with this email');
            } else if (error.code === 'auth/wrong-password') {
                throw new Error('Incorrect password');
            } else if (error.code === 'auth/invalid-email') {
                throw new Error('Invalid email address');
            } else if (error.code === 'auth/too-many-requests') {
                throw new Error('Too many failed login attempts. Please try again later.');
            } else {
                throw error;
            }
        }
    };

    const loginWithGoogle = async () => {
        try {
            setLoading(true);

            // Sign in with Google popup
            const userCredential = await signInWithPopup(auth, googleProvider);

            // Get ID token
            const token = await userCredential.user.getIdToken();
            setIdToken(token);

            // Check if user profile exists in Firestore
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

            if (!userDoc.exists()) {
                // Sign out the user if no profile exists
                await signOut(auth);
                throw new Error('No account found. Please contact administrator to create your account.');
            }

            const userData = userDoc.data();

            // Check if account is active
            if (!userData.isActive) {
                await signOut(auth);
                throw new Error('Account is disabled. Please contact administrator.');
            }

            const userProfile = {
                id: userCredential.user.uid,
                email: userCredential.user.email,
                fullName: userData.fullName,
                role: userData.role,
                department: userData.department,
                phone: userData.phone,
                isActive: userData.isActive,
                mustChangePassword: userData.mustChangePassword || false
            };

            setUser(userProfile);
            setLoading(false);

            return userProfile;
        } catch (error) {
            setLoading(false);
            console.error('Google Sign In error:', error);

            // Provide user-friendly error messages
            if (error.code === 'auth/popup-closed-by-user') {
                throw new Error('Sign in cancelled');
            } else if (error.code === 'auth/popup-blocked') {
                throw new Error('Popup blocked. Please allow popups for this site.');
            } else if (error.code === 'auth/cancelled-popup-request') {
                throw new Error('Sign in cancelled');
            } else if (error.message) {
                // Use custom error messages we set
                throw error;
            } else {
                throw new Error('Failed to sign in with Google. Please try again.');
            }
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setIdToken(null);
            setMustChangePassword(false);
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    };

    const getIdToken = async () => {
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    };

    const refreshToken = async () => {
        if (auth.currentUser) {
            const token = await auth.currentUser.getIdToken(true);
            setIdToken(token);
            return token;
        }
        return null;
    };

    const hasRole = (role) => {
        return user?.role === role;
    };

    const hasAnyRole = (...roles) => {
        return roles.includes(user?.role);
    };

    const refreshUserProfile = async () => {
        if (auth.currentUser) {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setUser(prev => ({
                    ...prev,
                    mustChangePassword: userData.mustChangePassword || false
                }));
                setMustChangePassword(userData.mustChangePassword || false);
            }
        }
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return { success: true };
        } catch (error) {
            console.error('Password reset error:', error);

            // Provide user-friendly error messages
            if (error.code === 'auth/user-not-found') {
                throw new Error('No account found with this email address.');
            } else if (error.code === 'auth/invalid-email') {
                throw new Error('Invalid email address.');
            } else {
                throw new Error('Failed to send password reset email. Please try again.');
            }
        }
    };

    const value = {
        user,
        loading,
        idToken,
        mustChangePassword,
        login,
        loginWithGoogle,
        logout,
        getIdToken,
        refreshToken,
        refreshUserProfile,
        hasRole,
        hasAnyRole,
        resetPassword,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
